import { useNavigate } from 'react-router-dom';
import { Check, Clock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { use_public_grooming_packages } from '@/features/grooming-package/hooks';
import { GROOMING_TIER_OPTIONS, type GroomingPackage } from '@/features/grooming-package/types';
import type { DiscoveryService } from '@/features/discovery/types';

const TIER_LABEL: Record<string, string> = Object.fromEntries(
  GROOMING_TIER_OPTIONS.map((t) => [t.value, t.label]),
);

/** Turn a package into the DiscoveryService shape the booking route expects. */
function as_service(pkg: GroomingPackage): DiscoveryService {
  return {
    id: pkg.service_id,
    module_type: 'GROOMER',
    name: pkg.name,
    description: pkg.description,
    price_inr: pkg.price_inr,
    duration_minutes: pkg.duration_minutes,
    // A package is an adapter over a real service, not a discovery result — it
    // never carries a distance of its own.
    distance_km: null,
    images: [],
  };
}

/**
 * Salon grooming packages (tiers) — Basic / Premium / Deluxe cards with the
 * included-items list, price, an optional struck-through MRP + derived discount
 * badge, and a Book CTA. Booking a tier books its underlying service_id.
 */
export function GroomingPackagesSection({
  vendor_id,
  vendor_name,
}: {
  vendor_id: string;
  vendor_name: string | null;
}) {
  const navigate = useNavigate();
  const packages_q = use_public_grooming_packages(vendor_id);
  const packages = packages_q.data ?? [];

  if (packages_q.isLoading) {
    return (
      <Card data-testid="grooming-packages">
        <CardHeader>
          <CardTitle>Grooming packages</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center p-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (packages.length === 0) return null;

  return (
    <Card data-testid="grooming-packages">
      <CardHeader>
        <CardTitle>Grooming packages</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {packages.map((pkg) => (
          <div
            key={pkg.id}
            className="flex flex-col gap-3 rounded-card border-2 border-brand-outline-variant/15 bg-secondary/20 p-4 shadow-clay"
            data-testid={`package-${pkg.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <StatusPill tone="info">{TIER_LABEL[pkg.tier] ?? pkg.tier}</StatusPill>
              {pkg.discount_pct != null && (
                <StatusPill tone="success" data-testid={`package-discount-${pkg.id}`}>
                  {pkg.discount_pct}% off
                </StatusPill>
              )}
            </div>

            <div className="min-w-0">
              <p className="font-heading text-base font-bold">{pkg.name}</p>
              {pkg.description && <p className="mt-0.5 text-xs text-muted-foreground">{pkg.description}</p>}
            </div>

            <ul className="flex flex-col gap-1.5">
              {pkg.included_items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto flex items-end justify-between gap-2 pt-1">
              <div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-lg font-bold tabular-nums">₹{pkg.price_inr}</span>
                  {pkg.mrp_inr != null && (
                    <span className="text-xs text-muted-foreground line-through tabular-nums">₹{pkg.mrp_inr}</span>
                  )}
                </div>
                {pkg.duration_minutes != null && (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" /> {pkg.duration_minutes} min
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="gradient"
                onClick={() =>
                  navigate(`/app/book/${pkg.service_id}`, {
                    state: { service: as_service(pkg), vendor_name, vendor_id },
                  })
                }
                data-testid={`book-package-${pkg.id}`}
              >
                Book
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
